import { inject, Injectable } from "@angular/core";
import { IPersistable, PersistMode } from "@barthauer/abc/core/data";
import { map, Observable, of, switchMap } from "rxjs";
import { DataService, ObjectKeys } from "../../services/data.service";
import { PermissionService } from "../../services/permission.service";
import { GUID } from "@barthauer/abc/core";
import { Actions, Permission } from "../enums";
import { UserRolePermissionViewModel } from "../../view-models/user-role-permission.view-model";
import { ROLES } from "@barthauer/custom/xmt-models";
import { PermissionContext } from "../../models/enumeration-list.model";

@Injectable({
    providedIn: 'root',
})
export class UserRightPopupService {
    private dataService = inject(DataService);
    private permissionService = inject(PermissionService);

    public actions: string[] =
        [
            Actions.VIEW,
            Actions.CREATE,
            Actions.UPDATE,
            Actions.DELETE,
        ]
    public role = '';

    private modulesMap: Map<string, UserRolePermissionViewModel> = new Map<string, UserRolePermissionViewModel>();
    public changedModulesMap: Map<string, Set<string>> = new Map<string, Set<string>>();
    private activeApprovalPermissionId = '';

    public loadPermissions(): Observable<{ modules: UserRolePermissionViewModel[], description: string }> {
        return this.permissionService
            .getRole()
            .pipe(
                switchMap((role) => {
                    const description = role.Description;
                    this.role = role.Value;
                    const modules$ = role?.Id ? this.permissionService.LoadPermission(role.Value) : of([]);
                    return modules$.pipe(
                        map(modules => ({ modules, description }))
                    )
                }))
    }

    public mergeModules(
        structure: UserRolePermissionViewModel[],
        values: UserRolePermissionViewModel[],
        parentId?: string
    ): UserRolePermissionViewModel[] {
        if(this.role!=ROLES.ADMIN) structure=structure.filter((struct)=>struct.Context!=PermissionContext.RoleRights);
        const valuesMap = new Map(values.map(v => [v.Context, v]));
        return structure.map(structItem => {
            const valueItem = valuesMap.get(structItem.Context);
            const merged: UserRolePermissionViewModel = {
                ...structItem,
                CanView: structItem.CanView ? valueItem?.CanView ?? false : null,
                CanCreate: structItem.CanCreate ? valueItem?.CanCreate ?? false : null,
                CanDelete: structItem.CanDelete ? valueItem?.CanDelete ?? false : null,
                CanUpdate: structItem.CanUpdate ? valueItem?.CanUpdate ?? false : null,
                IdRefAddress: valueItem?.IdRefAddress ?? sessionStorage.getItem('profileAddressId'),
                IdRefParentURPermission: parentId,
                Name: valueItem?.Name ?? this.role,
                Id: valueItem?.Id ?? GUID.newGUID(),
                isNew: valueItem?.Id ? false : true
            };
            if (valueItem && this.verifyActivePermission(valueItem, true)) this.activeApprovalPermissionId = merged.Id;
            if (structItem?.ChildUserRolePermissions) {
                merged.ChildUserRolePermissions = this.mergeModules(
                    structItem.ChildUserRolePermissions,
                    valueItem?.ChildUserRolePermissions || [],
                    merged.Id
                );
            }
            return merged;
        });
    }
    public buildModulesMap(modules: UserRolePermissionViewModel[]) {
        for (const module of modules) {
            this.modulesMap.set(module.Id!, module);

            if (module.ChildUserRolePermissions?.length) {
                this.buildModulesMap(module.ChildUserRolePermissions);
            }
        }
    }

    public updateModulesMap(module: UserRolePermissionViewModel, action: string): void {
        if (!module.isNew) {
            const auxModule = this.changedModulesMap.get(module.Id);
            if (auxModule) {
                if (!auxModule.delete(action)) auxModule.add(action);
                if (auxModule.size === 0) this.changedModulesMap.delete(module.Id);
            } else this.changedModulesMap.set(module.Id, new Set([action]));
        }
    }

    public verifyActivePermission(module: UserRolePermissionViewModel, isInitialize?: boolean): boolean {
        const baseCondition = [
            Permission.APPROVAL_REQUEST_STEP1,
            Permission.APPROVAL_REQUEST_STEP2,
            Permission.CHANGE_HARTEGRAD,
            Permission.CREATE_APPROVAL_REQUEST
        ].includes(module.Context as Permission)
        return isInitialize ? baseCondition && module.CanView === true : baseCondition;
    }
    public synchronizePermissions(module: UserRolePermissionViewModel, action: string): void {
        const activePermission = this.modulesMap.get(this.activeApprovalPermissionId);
        if (this.activeApprovalPermissionId ){
            if(module.Id != this.activeApprovalPermissionId)
                activePermission[action] = false;
            if (!this.changedModulesMap.delete(this.activeApprovalPermissionId)) 
            this.changedModulesMap.set(this.activeApprovalPermissionId, new Set([action]));
        }

        if (module.Id != this.activeApprovalPermissionId) {
            this.activeApprovalPermissionId = module.Id;
            if (!this.changedModulesMap.delete(module.Id)) this.changedModulesMap.set(module.Id, new Set([action]));
        }else this.activeApprovalPermissionId='';
    }

    public persistUserRolePermissions(): Observable<IPersistable> {
        const objectKey = ObjectKeys.USER_ROLE_PERMISSION;
        const newPermissions = [];
        const changedPermissions = [];
        Array.from(this.modulesMap.values()).forEach(module => {
            if (!module.isNew) {
                if (this.changedModulesMap.get(module.Id)) changedPermissions.push(this.buildPermission(module));
            }
            else newPermissions.push(this.buildPermission(module));
        });
        const query = [{
            objectKey: objectKey,
            persistMode: PersistMode.Insert,
            data: newPermissions
        },
        {
            objectKey: objectKey,
            persistMode: PersistMode.Update,
            data: changedPermissions
        }].filter(q => q.data.length > 0);
        return this.dataService.persistMultipleObjects(query);
    }

    private buildPermission(module: Partial<UserRolePermissionViewModel>): Partial<UserRolePermissionViewModel> {
        return {
            Id: module.Id,
            ...(!module.isNew 
             ? {
                ...Object.fromEntries(
                    Array.from(this.changedModulesMap.get(module.Id) ?? []).map(action => [action, module[action]])
                ),
             }
             : {
                Context: module.Context,
                Name: module.Name,
                IdRefAddress: module.IdRefAddress,
                ...(module.IdRefParentURPermission ? { IdRefParentURPermission: module.IdRefParentURPermission } : {}),
                ...Object.fromEntries(
                    Array.from(this.actions)
                        .map(action => [action, module[action]])
                )
             }
            )
        }
    }
    public resetModulesMap(): void {
        this.modulesMap.clear();
        this.changedModulesMap.clear();
        this.activeApprovalPermissionId = '';
    }
}